import type { ColDef, ColGroupDef } from "ag-grid-community";

import { ColumnType } from "@/queries/smart-tables/types";

export type SmartTableColumnDef<TData = unknown> = ColDef<TData> | ColGroupDef<TData>;
type SmartTableColumnGroupShow = "open" | "closed";

const SMART_TABLE_INPUTS_GROUP_ID_BASE = "__smart-table-inputs-group__";
const SMART_TABLE_INPUTS_GROUP_HEADER = "Inputs";
export const SMART_TABLE_INPUTS_GROUP_HEADER_CLASS = "smart-table-inputs-group-header";

export const isSmartTableInputsGroupId = (groupId?: string): boolean =>
  typeof groupId === "string" && groupId.startsWith(SMART_TABLE_INPUTS_GROUP_ID_BASE);

const SMART_TABLE_GROUP_HEADER_HEIGHT = 40;
const SMART_TABLE_LEAF_HEADER_HEIGHT = 40;

const SMART_TABLE_INPUTS_GROUP_CHILD_VISIBILITY = {
  CLOSED: "closed",
  OPEN: "open",
} as const;

export const SMART_TABLE_COLUMN_GROUP_HEADER_STYLE = {
  fontSize: "11px",
  fontWeight: 500,
  paddingInline: "6px",
} as const;

interface GroupConsecutiveInputColumnsParams<TData = unknown> {
  columns: readonly SmartTableColumnDef<TData>[];
  getColumnType: (column: ColDef<TData>) => string | null | undefined;
  isInputColumn?: (column: ColDef<TData>, columnType: string | null | undefined) => boolean;
  isAbsorbableGroup?: (column: ColGroupDef<TData>) => boolean;
  minGroupSize?: number;
  groupIdBase?: string;
  groupHeaderName?: string;
  groupDefaults?: Partial<Omit<ColGroupDef<TData>, "children" | "groupId" | "headerName">>;
}

export const isSmartTableColumnGroupDef = <TData = unknown>(
  column: SmartTableColumnDef<TData>,
): column is ColGroupDef<TData> => Array.isArray((column as ColGroupDef<TData>).children);

const getColumnDefKey = <TData = unknown>(column: SmartTableColumnDef<TData>): string | null => {
  if (isSmartTableColumnGroupDef(column)) {
    return typeof column.groupId === "string" ? column.groupId : null;
  }

  if (typeof column.field === "string" && column.field.length > 0) {
    return column.field;
  }

  if (typeof column.colId === "string" && column.colId.length > 0) {
    return column.colId;
  }

  return null;
};

const isInputColumnType = (columnType: string | null | undefined): boolean =>
  columnType === ColumnType.TEXT;

export const isAnchorMetricColumnGroup = <TData = unknown>(
  column: ColGroupDef<TData>,
  getColumnType: (column: ColDef<TData>) => string | null | undefined,
): boolean => {
  for (const child of column.children ?? []) {
    if (isSmartTableColumnGroupDef(child as SmartTableColumnDef<TData>)) {
      continue;
    }
    const childType = getColumnType(child as ColDef<TData>);
    if (childType === ColumnType.TRACE || childType === ColumnType.REQUEST) {
      return true;
    }
  }
  return false;
};

/**
 * Request/Trace groups stay top-level so their virtual execution-metadata
 * children are not reclassified as Inputs columns.
 */
export const isAbsorbableAnchorMetricColumnGroup = <TData = unknown>(
  _column: ColGroupDef<TData>,
  _getColumnType: (column: ColDef<TData>) => string | null | undefined,
): boolean => false;

/** @deprecated Prefer isAnchorMetricColumnGroup */
export const isTraceMetricColumnGroup = isAnchorMetricColumnGroup;

export const hasGroupedColumns = <TData = unknown>(
  columns: readonly SmartTableColumnDef<TData>[],
): boolean => columns.some(isSmartTableColumnGroupDef);

export const getSmartTableGroupedHeaderGridOptions = (hasGroupedHeaders: boolean) => ({
  headerHeight: SMART_TABLE_LEAF_HEADER_HEIGHT,
  ...(hasGroupedHeaders ? { groupHeaderHeight: SMART_TABLE_GROUP_HEADER_HEIGHT } : {}),
});

export const applyColumnGroupChildVisibility = <TColumn extends object>(
  children: readonly TColumn[],
): (TColumn & { columnGroupShow?: SmartTableColumnGroupShow })[] =>
  children.map((child, childIndex) =>
    childIndex === 0
      ? { ...child }
      : {
          ...child,
          columnGroupShow: SMART_TABLE_INPUTS_GROUP_CHILD_VISIBILITY.OPEN,
        },
  );

const createInputGroupId = <TData = unknown>(
  firstChild: SmartTableColumnDef<TData>,
  fallbackIndex: number,
  groupIdBase: string,
): string => {
  const firstChildKey = getColumnDefKey(firstChild);
  return `${groupIdBase}-${firstChildKey ?? fallbackIndex}`;
};

const createInputColumnGroup = <TData = unknown>({
  children,
  firstChild,
  fallbackIndex,
  groupIdBase,
  groupHeaderName,
  groupDefaults,
}: {
  children: readonly SmartTableColumnDef<TData>[];
  firstChild: SmartTableColumnDef<TData>;
  fallbackIndex: number;
  groupIdBase: string;
  groupHeaderName: string;
  groupDefaults?: Partial<Omit<ColGroupDef<TData>, "children" | "groupId" | "headerName">>;
}): ColGroupDef<TData> => ({
  headerName: groupHeaderName,
  groupId: createInputGroupId(firstChild, fallbackIndex, groupIdBase),
  openByDefault: true,
  marryChildren: true,
  headerStyle: SMART_TABLE_COLUMN_GROUP_HEADER_STYLE,
  headerClass: SMART_TABLE_INPUTS_GROUP_HEADER_CLASS,
  ...groupDefaults,
  children: applyColumnGroupChildVisibility(children),
});

export const groupConsecutiveInputColumns = <TData = unknown>({
  columns,
  getColumnType,
  isInputColumn = (_column, columnType) => isInputColumnType(columnType),
  isAbsorbableGroup = column => isAbsorbableAnchorMetricColumnGroup(column, getColumnType),
  minGroupSize = 2,
  groupIdBase = SMART_TABLE_INPUTS_GROUP_ID_BASE,
  groupHeaderName = SMART_TABLE_INPUTS_GROUP_HEADER,
  groupDefaults,
}: GroupConsecutiveInputColumnsParams<TData>): SmartTableColumnDef<TData>[] => {
  const groupedColumns: SmartTableColumnDef<TData>[] = [];
  let index = 0;

  while (index < columns.length) {
    const currentColumn = columns[index];

    if (!currentColumn) {
      index += 1;
      continue;
    }

    const currentColumnIsAbsorbableGroup =
      isSmartTableColumnGroupDef(currentColumn) && isAbsorbableGroup(currentColumn);

    if (isSmartTableColumnGroupDef(currentColumn) && !currentColumnIsAbsorbableGroup) {
      groupedColumns.push(currentColumn);
      index += 1;
      continue;
    }

    const currentColumnType = currentColumnIsAbsorbableGroup ? null : getColumnType(currentColumn);
    if (
      !currentColumnIsAbsorbableGroup &&
      !isInputColumn(currentColumn as ColDef<TData>, currentColumnType)
    ) {
      groupedColumns.push(currentColumn);
      index += 1;
      continue;
    }

    const runStart = index;
    let runEndExclusive = runStart + 1;
    let textColumnCount = currentColumnIsAbsorbableGroup ? 0 : 1;
    let absorbedGroupCount = currentColumnIsAbsorbableGroup ? 1 : 0;

    while (runEndExclusive < columns.length) {
      const nextColumn = columns[runEndExclusive];
      if (!nextColumn) {
        break;
      }

      if (isSmartTableColumnGroupDef(nextColumn)) {
        if (!isAbsorbableGroup(nextColumn)) {
          break;
        }
        absorbedGroupCount += 1;
        runEndExclusive += 1;
        continue;
      }

      const nextColumnType = getColumnType(nextColumn);
      if (!isInputColumn(nextColumn, nextColumnType)) {
        break;
      }

      textColumnCount += 1;
      runEndExclusive += 1;
    }

    const canFormInputsGroup =
      textColumnCount >= minGroupSize || (textColumnCount >= 1 && absorbedGroupCount >= 1);

    if (!canFormInputsGroup) {
      groupedColumns.push(currentColumn);
      index += 1;
      continue;
    }

    groupedColumns.push(
      createInputColumnGroup({
        children: columns.slice(runStart, runEndExclusive),
        firstChild: currentColumn,
        fallbackIndex: runStart,
        groupIdBase,
        groupHeaderName,
        groupDefaults,
      }),
    );

    index = runEndExclusive;
  }

  return groupedColumns;
};
